import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { environment } from '@environments/environment';
import { IFBRole, IFBTenant } from '@app/models/tenant';
import { CompanyService } from '@app/shared/services/company.service';

import { IFBPasswordPolicy, IFBShareSettings } from '@interfaces';
import { IFBIdentityProvidersResponse, IFBSsoIdentityProvider, IFBSsoStatus } from '@app/models/sso.model';

interface IFBApplicationSyncSettings {
  connectionId: string;
  allowEditingStartDate: boolean;
  allowDefaultStartDate: boolean;
  allowReopeningApplications: boolean;
  allowCandidateTracking: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TenantResource {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);
  private API_BASE_TENANT_URL = `https://${environment.AUTH_BASE}/api/tenants`;
  private SHORTENER_BASE_TENANT_URL = `https://${environment.SHORTENER_BASE}/api/tenants`;

  private url(role?: string): string {
    const rolePath = role ? `/roles/${role}` : '';
    const tenantId = this.companyService.data.id;
    return `${this.API_BASE_TENANT_URL}/${tenantId}${rolePath}`;
  }

  private get REFERRAL_BASE_TENANT_URL() {
    const tenantId = this.companyService.data.id;
    return `https://api.${environment.REFERRAL_BASE}/api/management/tenants/${tenantId}/share_settings`;
  }

  changeIdentityProviderStatus(id: string, payload: IFBSsoStatus) {
    return this.http.put<IFBSsoStatus>(`${this.url()}/identity_providers/${id}/status`, payload);
  }

  createIdentityProvider(payload: IFBSsoIdentityProvider) {
    return this.http.post<IFBSsoIdentityProvider>(this.url() + '/identity_providers', payload);
  }

  getDetails() {
    return this.http.get<IFBTenant>(this.url());
  }

  getIdentityProviders() {
    return this.http.get<IFBIdentityProvidersResponse>(`${this.url()}/identity_providers`);
  }

  updateRole(role: string, body: IFBRole) {
    return this.http.put<void>(this.url(role), body);
  }

  getPasswordPolicy() {
    return this.http.get<IFBPasswordPolicy>(`${this.url()}/password_policy`);
  }

  updatePasswordPolicy(policy: IFBPasswordPolicy) {
    return this.http.put<IFBPasswordPolicy>(`${this.url()}/password_policy`, policy);
  }

  getFraudProtectionState() {
    const url = `${this.SHORTENER_BASE_TENANT_URL}/${this.companyService.data.id}`;
    return this.http.get<{ viewProtection: string }>(url);
  }

  getShareSettings() {
    return this.http.get<IFBShareSettings>(this.REFERRAL_BASE_TENANT_URL);
  }

  saveShareSettings(body: IFBShareSettings) {
    return this.http.put<IFBShareSettings>(this.REFERRAL_BASE_TENANT_URL, body);
  }

  enableFraudProtection(body: any = {}) {
    const url = `${this.SHORTENER_BASE_TENANT_URL}/${this.companyService.data.id}`;
    body.viewProtection = 'RECAPTCHA';
    return this.http.put<void>(url, body);
  }

  disableFraudProtection(body: any = {}) {
    const url = `${this.SHORTENER_BASE_TENANT_URL}/${this.companyService.data.id}`;
    body.viewProtection = undefined;
    return this.http.put<void>(url, body);
  }

  saveIdentityProvider(payload: IFBSsoIdentityProvider) {
    return this.http.put<IFBSsoIdentityProvider>(this.url() + '/identity_providers/' + payload.id, payload);
  }

  getApplicationSyncSettings() {
    const tenantId = this.companyService.data.id;
    const url = `https://${environment.INTEGRATIONS_API_BASE}/api/ats/tenant/${tenantId}/application_sync_settings`;
    return this.http.get<IFBApplicationSyncSettings[]>(url);
  }
}
